/**
 * Permissions Constants
 * Permisos granulares del sistema para control de acceso
 */

export const PERMISSIONS = {
    // Registros
    VIEW_REGISTRATIONS: 'can_view_registrations',
    VIEW_REGISTRATION_MONITOR: 'can_view_registration_monitor',
    EXPORT_DATA: 'can_export_data',
    PRINT_BADGE: 'can_print_badge',
    VIEW_CREDENTIAL: 'can_view_credential',
    EDIT_REGISTRATION: 'can_edit_registration',

    // Eventos
    MANAGE_EVENTS: 'can_manage_events',
    VIEW_EVENTS: 'can_view_events',

    // Usuarios del Sistema
    MANAGE_USERS: 'can_manage_users',
    SUPERADMIN_DATA_CLEANUP: 'can_superadmin_data_cleanup',

    // Validación de Acceso (QR)
    VALIDATE_ACCESS: 'can_validate_access',

    // Reportes
    VIEW_REPORTS: 'can_view_reports',

    // Configuración
    MANAGE_SETTINGS: 'can_manage_settings',
    VIEW_SYSWEB_MONITOR: 'can_view_sysweb_monitor',
    VIEW_AUDIT_LOGS: 'can_view_audit_logs',

    // Borrado de Registros
    DELETE_REGISTRATION: 'can_delete_registration',

    // Evaluación de ponencias
    REVIEW_PAPERS: 'can_review_papers',
    MANAGE_COMMITTEE: 'can_manage_committee',
    VIEW_PAPER_MONITOR: 'can_view_paper_monitor',
    SEND_EVALUATOR_RECOGNITIONS: 'can_send_evaluator_recognitions',
    REVIEW_PAPERS_SIMULATOR: 'review:papers:simulator',
    DELETE_PAPER: 'can_delete_paper',

    // Reportería y Dashboard
    VIEW_DASHBOARD: 'can_view_dashboard',

    // Mesa de Ayuda
    VIEW_HELPDESK: 'can_view_helpdesk',
    MANUAL_PAYMENT: 'can_manual_payment',
    RESEND_TICKET: 'can_resend_ticket',
    UPDATE_PARTICIPANT: 'can_update_participant',
    ADD_VIP_REGISTRATION: 'can_add_vip',
    GLOBAL_HELPDESK: 'can_global_helpdesk',
    UPGRADE_PARTICIPANT: 'can_upgrade_participant',
} as const;

export type Permission = typeof PERMISSIONS[keyof typeof PERMISSIONS];

// Permisos que pueden delegarse con alcance global a usuarios que NO son SuperAdmin.
// Mantener esta lista estrictamente read-only/busqueda. El backend la usa como allowlist.
export const GLOBAL_DELEGABLE_PERMISSIONS = [
    PERMISSIONS.VIEW_REGISTRATION_MONITOR,
    PERMISSIONS.VIEW_PAPER_MONITOR,
    PERMISSIONS.VIEW_SYSWEB_MONITOR,
    PERMISSIONS.VIEW_AUDIT_LOGS,
    PERMISSIONS.VIEW_REPORTS,
    PERMISSIONS.GLOBAL_HELPDESK,
    PERMISSIONS.VIEW_EVENTS,
    PERMISSIONS.VIEW_DASHBOARD,
] as const;

// Permisos agrupados por rol sugerido (para UI de asignación)
export const ROLE_PRESETS = {
    'Gestor de Registro': [
        PERMISSIONS.VIEW_REGISTRATIONS,
        PERMISSIONS.EXPORT_DATA,
        PERMISSIONS.PRINT_BADGE,
        PERMISSIONS.VIEW_CREDENTIAL,
        PERMISSIONS.EDIT_REGISTRATION,
    ],
    'Validador de Acceso': [
        PERMISSIONS.VALIDATE_ACCESS,
        PERMISSIONS.VIEW_REGISTRATIONS,
    ],
    'Evaluador de ponencias': [
        PERMISSIONS.VIEW_REGISTRATIONS,
        PERMISSIONS.REVIEW_PAPERS,
        PERMISSIONS.DELETE_PAPER,
        PERMISSIONS.VIEW_DASHBOARD,
    ],
    'Administrador de Evento': [
        PERMISSIONS.VIEW_REGISTRATIONS,
        PERMISSIONS.EXPORT_DATA,
        PERMISSIONS.PRINT_BADGE,
        PERMISSIONS.VIEW_CREDENTIAL,
        PERMISSIONS.EDIT_REGISTRATION,
        PERMISSIONS.MANAGE_EVENTS,
        PERMISSIONS.VIEW_REPORTS,
        PERMISSIONS.DELETE_REGISTRATION,
        PERMISSIONS.REVIEW_PAPERS,
        PERMISSIONS.DELETE_PAPER,
        PERMISSIONS.VIEW_DASHBOARD,
    ],
} as const;

// Agrupación por módulos para la UI de permisos
export const PERMISSIONS_BY_MODULE = [
    {
        id: 'registros',
        label: 'Gestión de Registros',
        permissions: [
            { id: PERMISSIONS.VIEW_REGISTRATIONS, label: 'Ver Registros', description: 'Acceso para visualizar la lista de asistentes.' },
            { id: PERMISSIONS.VIEW_REGISTRATION_MONITOR, label: 'Monitor de Registros', description: 'Visualizar seguimiento operativo de registros en modo solo lectura.' },
            { id: PERMISSIONS.EDIT_REGISTRATION, label: 'Editar Registros', description: 'Permite modificar información de los asistentes.' },
            { id: PERMISSIONS.DELETE_REGISTRATION, label: 'Borrado Lógico', description: 'Permite ocultar registros del sistema.' },
            { id: PERMISSIONS.EXPORT_DATA, label: 'Exportar Datos', description: 'Descargar listas en Excel.' },
            { id: PERMISSIONS.PRINT_BADGE, label: 'Imprimir Gafete', description: 'Generar e imprimir gafetes.' },
        ]
    },
    {
        id: 'academico',
        label: 'Evaluación de ponencias',
        permissions: [
            { id: PERMISSIONS.REVIEW_PAPERS, label: 'Evaluar Ponencias', description: 'Acceso al visor y dictamen de documentos.' },
            { id: PERMISSIONS.DELETE_PAPER, label: 'Eliminar Ponencias', description: 'Permite borrar propuestas del sistema.' },
            { id: PERMISSIONS.MANAGE_COMMITTEE, label: 'Gestionar Comité Evaluador', description: 'Invitar evaluadores, asignar a ponencias, revocar accesos, ver dashboard del comité.' },
            { id: PERMISSIONS.VIEW_PAPER_MONITOR, label: 'Monitor de Ponencias', description: 'Visualizar el seguimiento academico y documental de ponencias en modo solo lectura.' },
            { id: PERMISSIONS.SEND_EVALUATOR_RECOGNITIONS, label: 'Enviar reconocimientos a evaluadores', description: 'Permite revisar y enviar correos de reconocimiento a evaluadores con evaluaciones registradas.' },
            { id: PERMISSIONS.REVIEW_PAPERS_SIMULATOR, label: 'Simulador de Emergencia', description: 'Permite bypass administrativo con auditoria reforzada.' },
        ]
    },
    {
        id: 'administracion',
        label: 'Administración',
        permissions: [
            { id: PERMISSIONS.MANAGE_EVENTS, label: 'Gestionar Eventos', description: 'Crear y editar información de congresos.' },
            { id: PERMISSIONS.MANAGE_USERS, label: 'Gestionar Usuarios', description: 'Administrar permisos de otros usuarios.' },
            { id: PERMISSIONS.SUPERADMIN_DATA_CLEANUP, label: 'Limpieza SuperAdmin de datos de prueba', description: 'Diagnosticar dependencias y limpiar datos de prueba autorizados por allowlist.' },
            { id: PERMISSIONS.MANAGE_SETTINGS, label: 'Configuración', description: 'Ajustes globales del sistema.' },
            { id: PERMISSIONS.VIEW_SYSWEB_MONITOR, label: 'Monitor SYSWEB', description: 'Visualizar la cola read-only de jobs SYSWEB.' },
            { id: PERMISSIONS.VIEW_AUDIT_LOGS, label: 'Bitácora de auditoría', description: 'Consultar eventos administrativos y trazabilidad operativa en modo solo lectura.' },
        ]
    },
    {
        id: 'reportes',
        label: 'Reportería',
        permissions: [
            { id: PERMISSIONS.VIEW_DASHBOARD, label: 'Ver Dashboard', description: 'Visualizar métricas estadísticas.' },
            { id: PERMISSIONS.VIEW_REPORTS, label: 'Generar Reportes', description: 'Acceso a la sección de analítica.' },
        ]
    },
    {
        id: 'helpdesk',
        label: 'Mesa de Ayuda',
        permissions: [
            { id: PERMISSIONS.VIEW_HELPDESK, label: 'Ver Mesa de Ayuda', description: 'Acceso al buscador y vista de soporte en sitio.' },
            { id: PERMISSIONS.VIEW_CREDENTIAL, label: 'Visualizar Credencial', description: 'Permite mostrar la credencial digital con QR en pantalla.' },
            { id: PERMISSIONS.MANUAL_PAYMENT, label: 'Confirmar Pagos Manuales', description: 'Permite marcar registros como pagados sin Sysweb.' },
            { id: PERMISSIONS.RESEND_TICKET, label: 'Reenviar Boletos', description: 'Disparar envío de correo con QR nuevamente.' },
            { id: PERMISSIONS.UPDATE_PARTICIPANT, label: 'Corregir Datos', description: 'Editar nombres o correos de asistentes en sitio.' },
            { id: PERMISSIONS.ADD_VIP_REGISTRATION, label: 'Registrar VIP', description: 'Dar de alta nuevos asistentes sin costo (Cortesía).' },
            { id: PERMISSIONS.GLOBAL_HELPDESK, label: 'Búsqueda Global', description: 'Permite buscar participantes en todos los eventos (Mesa de Ayuda Central).' },
            { id: PERMISSIONS.UPGRADE_PARTICIPANT, label: 'Upgrade de Participante', description: 'Permite convertir oyente UNACH sin kit a asistente UNACH.' },
        ]
    }
];

// Permiso especial para SuperAdmin (acceso total)
export const SUPER_ADMIN_PERMISSION = '*';
